import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksSwitch } from '../../components/liks/LiksSwitch/LiksSwitch';
import { LiksTextFormField } from '../../components/liks/LiksTextFormField/LiksTextFormField';
import { useI18n } from '../../i18n/useI18n';
import { isValidExcludedUrlPrefix } from './isValidExcludedUrlPrefix';

type StartCrawlExpertPanelProps = {
  expertPanelId: string;
  isExpertOpen: boolean;
  onToggleExpert: () => void;
  crawlMaxDepth: string;
  crawlMaxPages: string;
  crawlUseStartUrlAsPrefixFilter: boolean;
  crawlAllowSubdomains: boolean;
  crawlExcludedUrls: string[];
  prefixFilterSwitchId: string;
  allowSubdomainsSwitchId: string;
  isSubmitting: boolean;
  onCrawlMaxDepthChange: (value: string) => void;
  onCrawlMaxPagesChange: (value: string) => void;
  onCrawlMaxDepthBlur: () => void;
  onCrawlMaxPagesBlur: () => void;
  onCrawlUseStartUrlAsPrefixFilterChange: (value: boolean) => void;
  onCrawlAllowSubdomainsChange: (value: boolean) => void;
  onCrawlExcludedUrlsChange: (value: string[]) => void;
  preventInvalidNumericKey: (event: KeyboardEvent<HTMLInputElement>) => void;
};

export function StartCrawlExpertPanel({
  expertPanelId,
  isExpertOpen,
  onToggleExpert,
  crawlMaxDepth,
  crawlMaxPages,
  crawlUseStartUrlAsPrefixFilter,
  crawlAllowSubdomains,
  crawlExcludedUrls,
  prefixFilterSwitchId,
  allowSubdomainsSwitchId,
  isSubmitting,
  onCrawlMaxDepthChange,
  onCrawlMaxPagesChange,
  onCrawlMaxDepthBlur,
  onCrawlMaxPagesBlur,
  onCrawlUseStartUrlAsPrefixFilterChange,
  onCrawlAllowSubdomainsChange,
  onCrawlExcludedUrlsChange,
  preventInvalidNumericKey,
}: StartCrawlExpertPanelProps) {
  const { translate } = useI18n();
  const [newExcludedUrl, setNewExcludedUrl] = useState('');
  const [excludedUrlError, setExcludedUrlError] = useState<string | null>(null);

  const trimmedNewExcludedUrl = newExcludedUrl.trim();
  const canAddExcludedUrl =
    trimmedNewExcludedUrl !== '' &&
    !crawlExcludedUrls.includes(trimmedNewExcludedUrl);

  const handleAddExcludedUrl = () => {
    if (trimmedNewExcludedUrl === '') {
      return;
    }
    if (!isValidExcludedUrlPrefix(trimmedNewExcludedUrl)) {
      setExcludedUrlError(translate('knowledge.crawlExcludedUrlsInvalidError'));
      return;
    }
    if (crawlExcludedUrls.includes(trimmedNewExcludedUrl)) {
      setExcludedUrlError(
        translate('knowledge.crawlExcludedUrlsDuplicateError'),
      );
      return;
    }

    onCrawlExcludedUrlsChange([...crawlExcludedUrls, trimmedNewExcludedUrl]);
    setNewExcludedUrl('');
    setExcludedUrlError(null);
  };

  const handleRemoveExcludedUrl = (url: string) => {
    onCrawlExcludedUrlsChange(
      crawlExcludedUrls.filter((entry) => entry !== url),
    );
  };

  return (
    <div className="start-crawl-modal__expert">
      <button
        type="button"
        className="start-crawl-modal__expert-toggle"
        aria-expanded={isExpertOpen}
        aria-controls={expertPanelId}
        onClick={onToggleExpert}
        disabled={isSubmitting}
      >
        <span>{translate('knowledge.crawlExpertConfigTitle')}</span>
        <ChevronDown
          className={[
            'start-crawl-modal__expert-chevron',
            isExpertOpen && 'start-crawl-modal__expert-chevron--open',
          ]
            .filter(Boolean)
            .join(' ')}
          strokeWidth={2}
          aria-hidden
        />
      </button>

      {isExpertOpen ? (
        <div id={expertPanelId} className="start-crawl-modal__expert-panel">
          <div className="start-crawl-modal__field-group">
            <LiksTextFormField
              label={translate('knowledge.crawlMaxDepth')}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={crawlMaxDepth}
              onChange={onCrawlMaxDepthChange}
              onBlur={onCrawlMaxDepthBlur}
              onKeyDown={preventInvalidNumericKey}
              disabled={isSubmitting}
            />
            <p className="start-crawl-modal__hint">
              {translate('knowledge.crawlMaxDepthTooltip')}
            </p>
          </div>

          <div className="start-crawl-modal__field-group">
            <LiksTextFormField
              label={translate('knowledge.crawlMaxPages')}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={crawlMaxPages}
              onChange={onCrawlMaxPagesChange}
              onBlur={onCrawlMaxPagesBlur}
              onKeyDown={preventInvalidNumericKey}
              disabled={isSubmitting}
            />
            <p className="start-crawl-modal__hint">
              {translate('knowledge.crawlMaxPagesTooltip')}
            </p>
          </div>

          <div className="start-crawl-modal__switch-block">
            <div className="start-crawl-modal__switch-row">
              <LiksSwitch
                id={prefixFilterSwitchId}
                checked={crawlUseStartUrlAsPrefixFilter}
                aria-label={translate(
                  'knowledge.crawlUseStartUrlAsPrefixFilterLabel',
                )}
                onChange={onCrawlUseStartUrlAsPrefixFilterChange}
                disabled={isSubmitting}
              />
              <label
                className="start-crawl-modal__switch-label"
                htmlFor={prefixFilterSwitchId}
              >
                {translate('knowledge.crawlUseStartUrlAsPrefixFilterLabel')}
              </label>
            </div>
            <p className="start-crawl-modal__hint">
              {translate('knowledge.crawlUseStartUrlAsPrefixFilterDescription')}
            </p>

            {!crawlUseStartUrlAsPrefixFilter ? (
              <div className="start-crawl-modal__switch-child">
                <div className="start-crawl-modal__switch-row">
                  <LiksSwitch
                    id={allowSubdomainsSwitchId}
                    checked={crawlAllowSubdomains}
                    aria-label={translate(
                      'knowledge.crawlAllowSubdomainsLabel',
                    )}
                    onChange={onCrawlAllowSubdomainsChange}
                    disabled={isSubmitting}
                  />
                  <label
                    className="start-crawl-modal__switch-label"
                    htmlFor={allowSubdomainsSwitchId}
                  >
                    {translate('knowledge.crawlAllowSubdomainsLabel')}
                  </label>
                </div>
                <p className="start-crawl-modal__hint">
                  {translate('knowledge.crawlAllowSubdomainsDescription')}
                </p>
              </div>
            ) : null}
          </div>

          <div className="start-crawl-modal__excluded">
            <p className="start-crawl-modal__section-label">
              {translate('knowledge.crawlExcludedUrlsLabel')}
            </p>
            <p className="start-crawl-modal__hint">
              {translate('knowledge.crawlExcludedUrlsDescription')}
            </p>

            {crawlExcludedUrls.length > 0 ? (
              <ul className="start-crawl-modal__excluded-list">
                {crawlExcludedUrls.map((url) => (
                  <li key={url} className="start-crawl-modal__excluded-item">
                    <span className="start-crawl-modal__excluded-value">
                      {url}
                    </span>
                    <button
                      type="button"
                      className="start-crawl-modal__excluded-remove"
                      aria-label={translate(
                        'knowledge.crawlExcludedUrlsRemove',
                        { url },
                      )}
                      onClick={() => handleRemoveExcludedUrl(url)}
                      disabled={isSubmitting}
                    >
                      <Trash2 strokeWidth={2} aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            <div className="start-crawl-modal__add-excluded">
              <p className="start-crawl-modal__section-label">
                {translate('knowledge.crawlExcludedUrlsAddLabel')}
              </p>
              <div className="start-crawl-modal__add-excluded-row">
                <input
                  className="start-crawl-modal__excluded-input"
                  value={newExcludedUrl}
                  onChange={(event) => {
                    setNewExcludedUrl(event.target.value);
                    if (excludedUrlError) {
                      setExcludedUrlError(null);
                    }
                  }}
                  placeholder={translate(
                    'knowledge.crawlExcludedUrlsPlaceholder',
                  )}
                  aria-label={translate(
                    'knowledge.crawlExcludedUrlsInputLabel',
                  )}
                  disabled={isSubmitting}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleAddExcludedUrl();
                    }
                  }}
                />
                <LiksButton
                  type="button"
                  onClick={handleAddExcludedUrl}
                  disabled={!canAddExcludedUrl || isSubmitting}
                >
                  <Plus strokeWidth={2} aria-hidden />
                  {translate('knowledge.crawlExcludedUrlsAdd')}
                </LiksButton>
              </div>
              {excludedUrlError ? (
                <p className="start-crawl-modal__error" role="alert">
                  {excludedUrlError}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
